import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";

import { QuestionnaireResponse } from "@medplum/fhirtypes";
import { useMedplum } from "@medplum/react";

import { Group, Paper, Stack, Title } from "@mantine/core";

import { Loading } from "@/components/Loading";
import { TextWrapper } from "@/components/TextWrapper";
import { WordsDiff } from "@/components/WordsDiff";
import { getStateOfNewbornAnswersVersions } from "@/encounters/components/utils";
import { fetchAmendmentHistoryByQuestionnaireUrl } from "@/encounters/services/questionnaire-response";
import { Questionnaires } from "@/fhir/enums/questionnaires";
import NotFound from "@/NotFound";

import { useNewbornBirthingInformation } from "../../../../hooks/useNewbornBirthingInformation";

const StateOfNewbornDiff = ({ currentVersionIndex }: { currentVersionIndex: number }) => {
  const { encounterId } = useParams<{ encounterId: string }>();
  const medplum = useMedplum();

  const birthingInformation = useNewbornBirthingInformation();

  const [isFetchingInitialData, setIsFetchingInitialData] = useState(true);

  const [currentVersion, setCurrentVersion] = useState<QuestionnaireResponse>();
  const [previousVersion, setPreviousVersion] = useState<QuestionnaireResponse>();

  useEffect(() => {
    const getAmendmentHistory = async () => {
      medplum.invalidateSearches("QuestionnaireResponse");
      try {
        const [foundAmendmentHistory] = await fetchAmendmentHistoryByQuestionnaireUrl(
          medplum,
          Questionnaires.NewbornPostpartumStateOfNewborn,
          encounterId!,
        );

        if (!foundAmendmentHistory) return;

        setCurrentVersion(foundAmendmentHistory[currentVersionIndex]);
        setPreviousVersion(foundAmendmentHistory[currentVersionIndex + 1]);
      } finally {
        setIsFetchingInitialData(false);
      }
    };
    getAmendmentHistory();
  }, [currentVersionIndex, encounterId, medplum]);

  if (isFetchingInitialData) return <Loading />;

  if (!currentVersion || !previousVersion) return <NotFound />;

  const {
    previousName,
    currentName,
    previousStateOfNewborn,
    currentStateOfNewborn,
    previousSexDisplay,
    currentSexDisplay,
  } = getStateOfNewbornAnswersVersions(previousVersion, currentVersion);
  return (
    <Paper p="md" withBorder>
      <Stack mb="lg">
        <Title order={2}>State of Newborn</Title>
      </Stack>
      <Group grow align="stretch">
        <Paper p="md" withBorder>
          <Stack>
            <Title mb="lg" order={4} c="gray.6">
              Previous
            </Title>
            <Stack gap="md">
              <Stack gap="0">
                <TextWrapper fw={600}>Birth Date</TextWrapper>
                <WordsDiff
                  current={`${birthingInformation.birthDate}`}
                  previous={`${birthingInformation.birthDate}`}
                />
              </Stack>
              <Stack gap="0">
                <TextWrapper fw={600}>Time</TextWrapper>
                <WordsDiff
                  current={`${birthingInformation.birthTime}`}
                  previous={`${birthingInformation.birthTime}`}
                />
              </Stack>
              <Stack gap="0">
                <TextWrapper fw={600}>Time since Birth</TextWrapper>
                <WordsDiff
                  current={`${birthingInformation.timeSinceBirth} days`}
                  previous={`${birthingInformation.timeSinceBirth} days`}
                />
              </Stack>
              <Stack gap="0">
                <TextWrapper fw={600}>Sex</TextWrapper>
                <WordsDiff
                  showRemoved
                  current={String(currentSexDisplay)}
                  previous={String(previousSexDisplay)}
                />
              </Stack>
              <Stack gap="0">
                <TextWrapper fw={600}>Newborn's Name</TextWrapper>
                <WordsDiff
                  showRemoved
                  current={String(currentName)}
                  previous={String(previousName)}
                />
              </Stack>
              <Stack gap="0">
                <TextWrapper fw={600}>Newborn State(s) During Visit</TextWrapper>
                <WordsDiff
                  showRemoved
                  current={currentStateOfNewborn}
                  previous={previousStateOfNewborn}
                />
              </Stack>
            </Stack>
          </Stack>
        </Paper>
        <Paper p="md" withBorder>
          <Stack>
            <Title mb="lg" order={4} c="gray.6">
              Amended
            </Title>
            <Stack gap="md">
              <Stack gap="0">
                <TextWrapper fw={600}>Birth Date</TextWrapper>
                <WordsDiff
                  current={`${birthingInformation.birthDate}`}
                  previous={`${birthingInformation.birthDate}`}
                />
              </Stack>
              <Stack gap="0">
                <TextWrapper fw={600}>Time</TextWrapper>
                <WordsDiff
                  current={`${birthingInformation.birthTime}`}
                  previous={`${birthingInformation.birthTime}`}
                />
              </Stack>
              <Stack gap="0">
                <TextWrapper fw={600}>Time since Birth</TextWrapper>
                <WordsDiff
                  current={`${birthingInformation.timeSinceBirth} days`}
                  previous={`${birthingInformation.timeSinceBirth} days`}
                />
              </Stack>
              <Stack gap="0">
                <TextWrapper fw={600}>Sex</TextWrapper>
                <WordsDiff
                  current={String(currentSexDisplay)}
                  previous={String(previousSexDisplay)}
                />
              </Stack>
              <Stack gap="0">
                <TextWrapper fw={600}>Newborn's Name</TextWrapper>
                <WordsDiff current={String(currentName)} previous={String(previousName)} />
              </Stack>
              <Stack gap="0">
                <TextWrapper fw={600}>Newborn State(s) During Visit</TextWrapper>
                <WordsDiff current={currentStateOfNewborn} previous={previousStateOfNewborn} />
              </Stack>
            </Stack>
          </Stack>
        </Paper>
      </Group>
    </Paper>
  );
};

export default StateOfNewbornDiff;
